import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  },
  select: {
    fontSize: '13px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color: '#111827',
    cursor: 'pointer',
    outline: 'none',
  },
  svgContainer: {
    width: '100%',
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    backgroundColor: 'rgba(17, 24, 39, 0.92)',
    color: '#ffffff',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    pointerEvents: 'none',
    zIndex: 50,
    maxWidth: '220px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  tooltipLabel: {
    fontWeight: 600,
    marginBottom: '4px',
    fontSize: '12px',
    color: '#e5e7eb',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    fontSize: '12px',
  },
  tooltipOOC: {
    color: '#fca5a5',
    fontWeight: 600,
    marginTop: '4px',
    fontSize: '11px',
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginTop: '12px',
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#4b5563',
  },
  legendSwatch: {
    width: '14px',
    height: '3px',
    borderRadius: '2px',
    display: 'inline-block',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  targetInfo: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '8px',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  initialServiceVisit: 'Initial Service Visit',
  oneTime: 'One-Time',
  mosquitoTickControl: 'Mosquito & Tick Control',
};

const COLORS = {
  cusumPos: '#2563eb',
  cusumNeg: '#dc2626',
  ucl: '#9333ea',
  lcl: '#9333ea',
  zero: '#9ca3af',
  oocFillPos: 'rgba(239, 68, 68, 0.08)',
  oocFillNeg: 'rgba(34, 197, 94, 0.08)',
  oocDotPos: '#dc2626',
  oocDotNeg: '#16a34a',
};

export default function CUSUMChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedField, setSelectedField] = useState('');

  const fieldKeys = useMemo(() => {
    if (!data || !data.fields) return [];
    return Object.keys(data.fields);
  }, [data]);

  useEffect(() => {
    if (fieldKeys.length > 0 && !selectedField) {
      setSelectedField(fieldKeys[0]);
    }
  }, [fieldKeys, selectedField]);

  const fieldData = useMemo(() => {
    if (!data || !data.fields || !selectedField) return null;
    return data.fields[selectedField];
  }, [data, selectedField]);

  useEffect(() => {
    if (!svgRef.current || !fieldData || !data?.dates?.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 380;
    const margin = { top: 24, right: 24, bottom: 56, left: 56 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const { cusum_pos, cusum_neg, ucl, lcl } = fieldData;
    const dates = data.dates;

    const parseDate = d3.timeParse('%Y-%m-%d');
    const parsedDates = dates.map((d) => parseDate(d));

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerWidth]);

    const allValues = [...cusum_pos, ...cusum_neg, ucl, lcl];
    const yMin = Math.min(d3.min(allValues), lcl) * 1.15;
    const yMax = Math.max(d3.max(allValues), ucl) * 1.15;

    const yScale = d3.scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]).nice();

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Background shading for out-of-control regions
    // Upper OOC region (above UCL)
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(yMax))
      .attr('width', innerWidth)
      .attr('height', Math.max(0, yScale(ucl) - yScale(yMax)))
      .attr('fill', COLORS.oocFillPos);

    // Lower OOC region (below LCL)
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(lcl))
      .attr('width', innerWidth)
      .attr('height', Math.max(0, yScale(yMin) - yScale(lcl)))
      .attr('fill', COLORS.oocFillNeg);

    // Grid lines
    const yTicks = yScale.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .join('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero reference line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(0))
      .attr('y2', yScale(0))
      .attr('stroke', COLORS.zero)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // UCL line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(ucl))
      .attr('y2', yScale(ucl))
      .attr('stroke', COLORS.ucl)
      .attr('stroke-width', 1.2)
      .attr('stroke-dasharray', '6,4');

    g.append('text')
      .attr('x', innerWidth + 4)
      .attr('y', yScale(ucl))
      .attr('dy', '0.35em')
      .attr('font-size', '10px')
      .attr('fill', COLORS.ucl)
      .attr('font-weight', 500)
      .text('UCL');

    // LCL line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(lcl))
      .attr('y2', yScale(lcl))
      .attr('stroke', COLORS.lcl)
      .attr('stroke-width', 1.2)
      .attr('stroke-dasharray', '6,4');

    g.append('text')
      .attr('x', innerWidth + 4)
      .attr('y', yScale(lcl))
      .attr('dy', '0.35em')
      .attr('font-size', '10px')
      .attr('fill', COLORS.lcl)
      .attr('font-weight', 500)
      .text('LCL');

    // C+ line
    const linePos = d3
      .line()
      .x((_, i) => xScale(parsedDates[i]))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(cusum_pos)
      .attr('fill', 'none')
      .attr('stroke', COLORS.cusumPos)
      .attr('stroke-width', 2)
      .attr('d', linePos);

    // C- line
    const lineNeg = d3
      .line()
      .x((_, i) => xScale(parsedDates[i]))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(cusum_neg)
      .attr('fill', 'none')
      .attr('stroke', COLORS.cusumNeg)
      .attr('stroke-width', 2)
      .attr('d', lineNeg);

    // Out-of-control dots for C+
    cusum_pos.forEach((val, i) => {
      if (val > ucl) {
        g.append('circle')
          .attr('cx', xScale(parsedDates[i]))
          .attr('cy', yScale(val))
          .attr('r', 4.5)
          .attr('fill', COLORS.oocDotPos)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5);
      }
    });

    // Out-of-control dots for C-
    cusum_neg.forEach((val, i) => {
      if (val < lcl) {
        g.append('circle')
          .attr('cx', xScale(parsedDates[i]))
          .attr('cy', yScale(val))
          .attr('r', 4.5)
          .attr('fill', COLORS.oocDotNeg)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5);
      }
    });

    // X axis
    const tickCount = Math.min(dates.length, 8);
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(tickCount)
      .tickFormat(d3.timeFormat('%b %d'));

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .call((sel) => {
        sel.select('.domain').attr('stroke', '#d1d5db');
        sel.selectAll('.tick line').attr('stroke', '#d1d5db');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '11px');
      });

    // Y axis
    const yAxis = d3.axisLeft(yScale).ticks(6);

    g.append('g')
      .call(yAxis)
      .call((sel) => {
        sel.select('.domain').attr('stroke', '#d1d5db');
        sel.selectAll('.tick line').attr('stroke', '#d1d5db');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '11px');
      });

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -42)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('CUSUM Statistic');

    // Hover overlay
    const overlay = g
      .append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .attr('cursor', 'crosshair');

    overlay.on('mousemove', (event) => {
      const [mx] = d3.pointer(event);
      const hoveredDate = xScale.invert(mx);
      const bisector = d3.bisector((d) => d).left;
      let idx = bisector(parsedDates, hoveredDate);
      if (idx >= parsedDates.length) idx = parsedDates.length - 1;
      if (idx > 0) {
        const d0 = parsedDates[idx - 1];
        const d1 = parsedDates[idx];
        if (hoveredDate - d0 < d1 - hoveredDate) idx = idx - 1;
      }

      const posVal = cusum_pos[idx];
      const negVal = cusum_neg[idx];
      const isOOC = posVal > ucl || negVal < lcl;

      const containerRect = containerRef.current?.getBoundingClientRect();
      const svgRect = svgRef.current?.getBoundingClientRect();
      if (!containerRect || !svgRect) return;

      const px = xScale(parsedDates[idx]) + margin.left;
      const svgDisplayWidth = svgRect.width;
      const scaleFactor = svgDisplayWidth / 700;
      const tooltipX = px * scaleFactor;
      const tooltipY = 20;

      setTooltip({
        x: tooltipX,
        y: tooltipY,
        date: dates[idx],
        posVal: posVal.toFixed(3),
        negVal: negVal.toFixed(3),
        isOOC,
        alignRight: tooltipX > svgDisplayWidth * 0.65,
      });
    });

    overlay.on('mouseleave', () => {
      setTooltip(null);
    });
  }, [fieldData, data, selectedField]);

  if (!data || !data.dates || !data.fields || Object.keys(data.fields).length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>CUSUM Control Chart</h3>
        <p style={styles.subtitle}>Cumulative sum detection of persistent price drift</p>
        <div style={styles.emptyState}>No CUSUM data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>CUSUM Control Chart</h3>
      <p style={styles.subtitle}>Cumulative sum detection of persistent price drift</p>

      <div style={styles.controls}>
        <span style={styles.label}>Field:</span>
        <select
          style={styles.select}
          value={selectedField}
          onChange={(e) => {
            setSelectedField(e.target.value);
            setTooltip(null);
          }}
        >
          {fieldKeys.map((key) => (
            <option key={key} value={key}>
              {FIELD_LABELS[key] || key}
            </option>
          ))}
        </select>
        {fieldData && (
          <span style={styles.targetInfo}>
            Target mean: <strong>${fieldData.target?.toFixed(2)}</strong>
          </span>
        )}
      </div>

      <div style={styles.svgContainer} ref={containerRef}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 380"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
        {tooltip && (
          <div
            style={{
              ...styles.tooltip,
              left: tooltip.alignRight ? undefined : `${tooltip.x + 12}px`,
              right: tooltip.alignRight
                ? `${(containerRef.current?.offsetWidth || 0) - tooltip.x + 12}px`
                : undefined,
              top: `${tooltip.y}px`,
            }}
          >
            <div style={styles.tooltipLabel}>{tooltip.date}</div>
            <div style={styles.tooltipRow}>
              <span style={{ color: '#93c5fd' }}>C+ (pos):</span>
              <span>{tooltip.posVal}</span>
            </div>
            <div style={styles.tooltipRow}>
              <span style={{ color: '#fca5a5' }}>C- (neg):</span>
              <span>{tooltip.negVal}</span>
            </div>
            {tooltip.isOOC && (
              <div style={styles.tooltipOOC}>Out of control</div>
            )}
          </div>
        )}
      </div>

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: COLORS.cusumPos }} />
          <span>C+ (upward drift)</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, backgroundColor: COLORS.cusumNeg }} />
          <span>C- (downward drift)</span>
        </div>
        <div style={styles.legendItem}>
          <span
            style={{
              ...styles.legendSwatch,
              backgroundColor: COLORS.ucl,
              borderTop: '1px dashed',
              height: '0px',
              width: '14px',
              borderColor: COLORS.ucl,
            }}
          />
          <span>UCL / LCL</span>
        </div>
        <div style={styles.legendItem}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: COLORS.oocDotPos,
              display: 'inline-block',
            }}
          />
          <span>Out-of-control point</span>
        </div>
      </div>
    </div>
  );
}
